import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Star, MapPin, ChevronDown, Search, Users, Award, Filter } from 'lucide-react';
import Logo from './Logo';
import { realtors, US_STATES, getRealtorsByState, getStatesWithRealtors } from '../data/realtors';

const RealtorListPage = () => {
  const [selectedState, setSelectedState] = useState('ALL');
  const [searchQuery, setSearchQuery] = useState('');
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [filteredRealtors, setFilteredRealtors] = useState(realtors);

  const statesWithRealtors = getStatesWithRealtors();

  useEffect(() => {
    let results = getRealtorsByState(selectedState);

    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      results = results.filter(r =>
        r.name.toLowerCase().includes(query) ||
        r.city.toLowerCase().includes(query) ||
        r.specialties.some(s => s.toLowerCase().includes(query))
      );
    }

    setFilteredRealtors(results);
  }, [selectedState, searchQuery]);

  const getStateName = (code) => {
    const state = US_STATES.find(s => s.code === code);
    return state ? state.name : code;
  };

  return (
    <div className="min-h-screen bg-[#0a0f1c] text-white">
      <style>{`
        .gradient-text {
          background: linear-gradient(135deg, #10b981 0%, #34d399 50%, #6ee7b7 100%);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
        }

        .realtor-card {
          background: linear-gradient(135deg, rgba(20, 27, 45, 0.9) 0%, rgba(15, 23, 42, 0.95) 100%);
          border: 1px solid rgba(255, 255, 255, 0.08);
          transition: all 0.3s ease;
        }

        .realtor-card:hover {
          transform: translateY(-8px);
          border-color: rgba(16, 185, 129, 0.4);
          box-shadow: 0 20px 40px rgba(0, 0, 0, 0.4), 0 0 40px rgba(16, 185, 129, 0.1);
        }

        .profile-image {
          border: 3px solid transparent;
          background: linear-gradient(#141b2d, #141b2d) padding-box,
                      linear-gradient(135deg, #10b981, #34d399) border-box;
        }

        .state-badge {
          background: linear-gradient(135deg, rgba(16, 185, 129, 0.2) 0%, rgba(16, 185, 129, 0.1) 100%);
          border: 1px solid rgba(16, 185, 129, 0.3);
        }

        .filter-btn {
          background: rgba(20, 27, 45, 0.8);
          border: 1px solid rgba(255, 255, 255, 0.1);
          transition: all 0.2s ease;
        }

        .filter-btn:hover, .filter-btn.active {
          background: rgba(16, 185, 129, 0.15);
          border-color: rgba(16, 185, 129, 0.4);
        }

        .dropdown-menu {
          background: rgba(20, 27, 45, 0.98);
          border: 1px solid rgba(255, 255, 255, 0.1);
          backdrop-filter: blur(20px);
        }

        .dropdown-item:hover {
          background: rgba(16, 185, 129, 0.15);
        }

        .hero-gradient {
          background: linear-gradient(135deg, rgba(16, 185, 129, 0.15) 0%, transparent 50%),
                      linear-gradient(225deg, rgba(59, 130, 246, 0.1) 0%, transparent 50%);
        }

        @keyframes float {
          0%, 100% { transform: translateY(0px); }
          50% { transform: translateY(-10px); }
        }

        .float-animation {
          animation: float 3s ease-in-out infinite;
        }
      `}</style>

      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 px-6 py-4 bg-[#0a0f1c]/90 backdrop-blur-md border-b border-gray-800/50">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 text-white hover:text-gray-300 transition-colors">
            <ArrowLeft className="w-5 h-5" />
            <span className="hidden sm:inline">Trang Chủ</span>
          </Link>
          <Logo />
          <div className="flex items-center gap-4">
            <Link
              to="/blog"
              className="text-white/80 hover:text-white font-medium transition-colors"
            >
              Blog
            </Link>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="pt-24 pb-12 px-6 hero-gradient relative overflow-hidden">
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute top-20 left-10 w-72 h-72 bg-emerald-500/10 rounded-full blur-3xl"></div>
          <div className="absolute bottom-0 right-10 w-96 h-96 bg-blue-500/10 rounded-full blur-3xl"></div>
        </div>

        <div className="max-w-7xl mx-auto relative z-10">
          <div className="text-center max-w-3xl mx-auto">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-emerald-500/10 border border-emerald-500/20 mb-6">
              <Users className="w-4 h-4 text-emerald-400" />
              <span className="text-emerald-400 text-sm font-medium">Đội Ngũ Realtor Chọn Lọc</span>
            </div>

            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6">
              <span className="text-white">Realtor </span>
              <span className="gradient-text">Uy Tín</span>
            </h1>

            <p className="text-lg md:text-xl text-gray-400 mb-8 leading-relaxed">
              Kết nối với các Realtor người Việt được đánh giá cao nhất tại Mỹ.
              Chuyên nghiệp, tận tâm, và hiểu rõ nhu cầu của bạn.
            </p>

            {/* Stats */}
            <div className="flex flex-wrap justify-center gap-8 mb-8">
              <div className="text-center">
                <div className="text-3xl font-bold text-emerald-400">{realtors.length}+</div>
                <div className="text-gray-500 text-sm">Realtor Uy Tín</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-emerald-400">{statesWithRealtors.length}</div>
                <div className="text-gray-500 text-sm">Tiểu Bang</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-emerald-400">4.8+</div>
                <div className="text-gray-500 text-sm">Đánh Giá TB</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Filters Section */}
      <section className="py-6 px-6 border-b border-gray-800/50 sticky top-[72px] bg-[#0a0f1c]/95 backdrop-blur-md z-40">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col md:flex-row gap-4 items-center justify-between">
            {/* Search */}
            <div className="relative w-full md:w-96">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />
              <input
                type="text"
                placeholder="Tìm theo tên, thành phố, chuyên môn..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-12 pr-4 py-3 bg-[#141b2d] border border-gray-700 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:border-emerald-500/50 transition-colors"
              />
            </div>

            {/* State Filter Dropdown */}
            <div className="relative w-full md:w-auto">
              <button
                onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                className="filter-btn flex items-center justify-between gap-3 px-5 py-3 rounded-xl w-full md:w-64"
              >
                <div className="flex items-center gap-2">
                  <Filter className="w-4 h-4 text-emerald-400" />
                  <span className="text-white">
                    {selectedState === 'ALL' ? 'Tất Cả Tiểu Bang' : getStateName(selectedState)}
                  </span>
                </div>
                <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform ${isDropdownOpen ? 'rotate-180' : ''}`} />
              </button>

              {isDropdownOpen && (
                <div className="dropdown-menu absolute top-full left-0 right-0 mt-2 rounded-xl shadow-2xl max-h-96 overflow-y-auto z-50">
                  <button
                    onClick={() => { setSelectedState('ALL'); setIsDropdownOpen(false); }}
                    className={`dropdown-item w-full px-4 py-3 text-left flex items-center gap-3 ${selectedState === 'ALL' ? 'text-emerald-400' : 'text-white'}`}
                  >
                    <MapPin className="w-4 h-4" />
                    Tất Cả Tiểu Bang
                  </button>
                  <div className="border-t border-gray-700/50 my-1"></div>
                  {US_STATES.map(state => {
                    const hasRealtors = statesWithRealtors.some(s => s.code === state.code);
                    return (
                      <button
                        key={state.code}
                        onClick={() => { setSelectedState(state.code); setIsDropdownOpen(false); }}
                        disabled={!hasRealtors}
                        className={`dropdown-item w-full px-4 py-2.5 text-left flex items-center justify-between ${
                          selectedState === state.code ? 'text-emerald-400' : hasRealtors ? 'text-white' : 'text-gray-600 cursor-not-allowed'
                        }`}
                      >
                        <span>{state.name}</span>
                        {hasRealtors && (
                          <span className="text-xs text-emerald-400/60">
                            {getRealtorsByState(state.code).length}
                          </span>
                        )}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          </div>

          {/* Active Filters */}
          {(selectedState !== 'ALL' || searchQuery) && (
            <div className="flex flex-wrap gap-2 mt-4">
              {selectedState !== 'ALL' && (
                <span className="state-badge px-3 py-1 rounded-full text-sm text-emerald-400 flex items-center gap-2">
                  <MapPin className="w-3 h-3" />
                  {getStateName(selectedState)}
                  <button onClick={() => setSelectedState('ALL')} className="hover:text-white">&times;</button>
                </span>
              )}
              {searchQuery && (
                <span className="state-badge px-3 py-1 rounded-full text-sm text-emerald-400 flex items-center gap-2">
                  <Search className="w-3 h-3" />
                  "{searchQuery}"
                  <button onClick={() => setSearchQuery('')} className="hover:text-white">&times;</button>
                </span>
              )}
            </div>
          )}
        </div>
      </section>

      {/* Realtors Grid */}
      <section className="py-12 px-6">
        <div className="max-w-7xl mx-auto">
          {filteredRealtors.length > 0 ? (
            <>
              <p className="text-gray-500 mb-8">
                Hiển thị <span className="text-white font-semibold">{filteredRealtors.length}</span> Realtor
                {selectedState !== 'ALL' && <span> tại <span className="text-emerald-400">{getStateName(selectedState)}</span></span>}
              </p>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {filteredRealtors.map(realtor => (
                  <Link
                    key={realtor.id}
                    to={`/realtor/${realtor.slug}`}
                    className="realtor-card rounded-2xl overflow-hidden group"
                  >
                    {/* Card Header with gradient */}
                    <div className="h-24 bg-gradient-to-r from-emerald-600/20 via-emerald-500/10 to-blue-600/20 relative">
                      <div className="absolute -bottom-12 left-6">
                        <img
                          src={realtor.photo}
                          alt={realtor.name}
                          className="profile-image w-24 h-24 rounded-full object-cover"
                        />
                      </div>
                      <div className="absolute top-4 right-4">
                        <span className="state-badge px-3 py-1 rounded-full text-xs font-medium text-emerald-400">
                          {realtor.state}
                        </span>
                      </div>
                    </div>

                    {/* Card Content */}
                    <div className="pt-14 pb-6 px-6">
                      <h3 className="text-xl font-bold text-white mb-1 group-hover:text-emerald-400 transition-colors">
                        {realtor.name}
                      </h3>
                      <p className="text-gray-500 text-sm mb-3">{realtor.title}</p>

                      {/* Location */}
                      <div className="flex items-center gap-2 text-gray-400 text-sm mb-3">
                        <MapPin className="w-4 h-4 text-emerald-500" />
                        <span>{realtor.city}, {getStateName(realtor.state)}</span>
                      </div>

                      {/* Rating */}
                      <div className="flex items-center gap-2 mb-4">
                        <div className="flex items-center gap-1">
                          <Star className="w-4 h-4 text-yellow-400 fill-yellow-400" />
                          <span className="text-white font-semibold">{realtor.rating}</span>
                        </div>
                        <span className="text-gray-500 text-sm">({realtor.reviewCount} đánh giá)</span>
                      </div>

                      {/* Specialties */}
                      <div className="flex flex-wrap gap-2 mb-4">
                        {realtor.specialties.slice(0, 2).map((specialty, idx) => (
                          <span key={idx} className="px-2 py-1 bg-gray-800/50 rounded-lg text-xs text-gray-400">
                            {specialty}
                          </span>
                        ))}
                        {realtor.specialties.length > 2 && (
                          <span className="px-2 py-1 bg-gray-800/50 rounded-lg text-xs text-gray-400">
                            +{realtor.specialties.length - 2}
                          </span>
                        )}
                      </div>

                      {/* Stats */}
                      <div className="flex items-center justify-between pt-4 border-t border-gray-800">
                        <div className="text-center">
                          <div className="text-emerald-400 font-bold">{realtor.yearsExperience}</div>
                          <div className="text-gray-500 text-xs">Năm KN</div>
                        </div>
                        <div className="text-center">
                          <div className="text-emerald-400 font-bold">{realtor.transactionsCompleted}</div>
                          <div className="text-gray-500 text-xs">Giao Dịch</div>
                        </div>
                        <div className="text-center">
                          <div className="flex gap-1">
                            {realtor.languages.slice(0, 2).map((lang, idx) => (
                              <span key={idx} className="text-xs text-gray-400">
                                {lang === 'Vietnamese' ? 'VI' : lang === 'English' ? 'EN' : lang.slice(0, 2).toUpperCase()}
                                {idx < Math.min(realtor.languages.length, 2) - 1 && '/'}
                              </span>
                            ))}
                          </div>
                          <div className="text-gray-500 text-xs">Ngôn Ngữ</div>
                        </div>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            </>
          ) : (
            <div className="text-center py-20">
              <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-gray-800/50 flex items-center justify-center">
                <Users className="w-10 h-10 text-gray-600" />
              </div>
              <h3 className="text-xl font-semibold text-white mb-2">Không tìm thấy Realtor</h3>
              <p className="text-gray-500 mb-6">
                {selectedState !== 'ALL'
                  ? `Chưa có Realtor tại ${getStateName(selectedState)}. Vui lòng chọn tiểu bang khác.`
                  : 'Vui lòng thử tìm kiếm với từ khóa khác.'}
              </p>
              <button
                onClick={() => { setSelectedState('ALL'); setSearchQuery(''); }}
                className="px-6 py-3 bg-emerald-500 hover:bg-emerald-600 rounded-xl text-white font-medium transition-colors"
              >
                Xem Tất Cả Realtor
              </button>
            </div>
          )}
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 px-6 bg-gradient-to-b from-transparent to-emerald-900/10">
        <div className="max-w-4xl mx-auto text-center">
          <Award className="w-12 h-12 text-emerald-400 mx-auto mb-6" />
          <h2 className="text-2xl md:text-3xl font-bold text-white mb-4">
            Bạn là Realtor Uy Tín?
          </h2>
          <p className="text-gray-400 mb-8 max-w-2xl mx-auto">
            Tham gia mạng lưới Realtor người Việt của chúng tôi và kết nối với hàng nghìn khách hàng tiềm năng đang tìm kiếm người đồng hành trong hành trình mua nhà.
          </p>
          <Link
            to="/"
            className="inline-flex items-center gap-2 px-8 py-4 bg-emerald-500 hover:bg-emerald-600 rounded-xl text-white font-semibold transition-colors"
          >
            Liên Hệ Hợp Tác
          </Link>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 px-6 border-t border-gray-800">
        <div className="max-w-7xl mx-auto text-center text-gray-500 text-sm">
          © 2025 Nhamy.us - Nền tảng kết nối Realtor uy tín cho cộng đồng người Việt tại Mỹ
        </div>
      </footer>
    </div>
  );
};

export default RealtorListPage;
